/**
 * Client-side authorization utilities
 * Provides consistent permission checking across components
 */

export interface AuthUser {
  uid: string;
  // Accept both null and undefined so AuthContext.AuthUser is compatible
  email?: string | null;
  displayName?: string | null;
  isAdmin?: boolean;
  role?: string;
}

export interface ProjectData {
  userId?: string;
  editorId?: string;
  canEdit?: boolean;
  canEditTasks?: boolean;
}

/**
 * Check if user can edit a project
 * Uses server-provided canEdit field as primary source
 * Falls back to client-side checks for compatibility
 */
export function canEditProject(user: AuthUser | null, project: ProjectData | null): boolean {
  if (!user || !user.uid || !project) {
    return false;
  }

  // Primary: Use server-provided canEdit field (most secure)
  if (typeof project.canEdit === 'boolean') {
    return project.canEdit;
  }

  // Fallback: Client-side permission check
  // Note: This should be removed once all queries include canEdit field
  const isOwner = user.uid === project.userId;
  const isAssignedEditor = user.uid === project.editorId;
  const isAdminUser = user.isAdmin || user.role === 'admin';

  return isOwner || isAssignedEditor || isAdminUser;
}

/**
 * Get user role for display purposes
 */
export function getUserRole(user: AuthUser | null, project: ProjectData | null): 'creator' | 'editor' | 'unauthorized' {
  if (!user || !user.uid || !project) {
    return 'unauthorized';
  }

  const isOwner = user.uid === project.userId;
  const isAdminUser = user.isAdmin || user.role === 'admin' || user.role === 'superadmin';

  // Primary: Use server-provided canEditTasks if available (handles editorId normalization)
  if (!isOwner && typeof project.canEditTasks === 'boolean') {
    return project.canEditTasks || isAdminUser ? 'editor' : 'unauthorized';
  }

  // Fallback: Client-side assignment checks
  const userEmailLower = String(user.email || '').toLowerCase();
  const editorIdString = String(project.editorId || '');
  const isAssignedEditor =
    (user.uid === editorIdString) ||
    (!!userEmailLower && userEmailLower === editorIdString.toLowerCase()) ||
    // Some queries include an embedded editor object
    (!!userEmailLower && String((project as any)?.editor?.email || '').toLowerCase() === userEmailLower);

  if (isOwner) {
    return 'creator';
  } else if (isAssignedEditor || isAdminUser) {
    return 'editor';
  } else {
    return 'unauthorized';
  }
}

/**
 * Hook for consistent authorization checking
 */
export function useProjectAuth(user: AuthUser | null, project: ProjectData | null) {
  const canEdit = canEditProject(user, project);
  const userRole = getUserRole(user, project);
  
  return {
    canEdit,
    userRole,
    isAuthorized: userRole !== 'unauthorized'
  };
}
